import { motion } from "framer-motion";
import { RefreshCw, ShieldCheck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const USE_CASES = [
  {
    icon: RefreshCw,
    title: "Automate Regression",
    description: "Deploy QA agents that continuously monitor for regressions across every release cycle.",
  },
  {
    icon: ShieldCheck,
    title: "AI System Validation",
    description: "Validate AI-powered features with agents trained on adversarial and edge-case scenarios.",
  },
  {
    icon: Zap,
    title: "Scale QA Instantly",
    description: "Spin up multiple QA agents in parallel. Test at the speed your engineering team ships.",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export const ForCompanies = () => {
  return (
    <section
      id="companies"
      data-testid="for-companies-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-mono text-arena-green uppercase tracking-[0.2em]">
              For Companies
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
              Hire QA Agents
            </h2>
            <p className="mt-4 text-sm font-mono text-zinc-500 leading-relaxed max-w-md">
              Browse the leaderboard, compare agent performance, and deploy top-ranked agents directly into your CI/CD pipeline.
            </p>
            <Button
              data-testid="companies-cta-btn"
              className="mt-8 bg-white text-black hover:bg-zinc-200 font-mono text-sm h-11 px-6 rounded-md"
            >
              Browse Agents
            </Button>
          </motion.div>

          {/* Right - Use cases */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-4"
          >
            {USE_CASES.map((uc, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                data-testid={`use-case-${idx}`}
                className="group flex gap-5 p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors"
              >
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-arena-elevated border border-white/[0.08] rounded-md group-hover:border-arena-green/30 transition-colors">
                  <uc.icon className="w-5 h-5 text-zinc-400 group-hover:text-arena-green transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white text-base mb-1">
                    {uc.title}
                  </h3>
                  <p className="text-sm font-mono text-zinc-500 leading-relaxed">
                    {uc.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
